import { useInterval } from '@mantine/hooks'
import { createSelector } from '@reduxjs/toolkit'
import { useSearchParams } from 'next/navigation'
import React from 'react'

import { useAppSelector } from '@flecto-io/core'
import useAsyncAppDispatch from '@flecto-io/core/hooks/asyncAppDispatch'
import {
  getAllBookingsInsurances,
  refreshUserMeta,
} from '@flecto-io/core/src/actions'
import { type RootState } from '@flecto-io/core/src/store/redux'
import { claimStoredTokens } from '@flecto/api/src/services/tokens'

import { useInsuranceStatus } from '../../../../booking-insurances'

interface InsurancePollingOptions {
  onUpdate?: () => void
}

export default function useRefreshInsurancePending({
  onUpdate,
}: InsurancePollingOptions = {}) {
  const dispatch = useAsyncAppDispatch()
  const interval = useInterval(() => loadInsurance(), 3000)
  const router = useSearchParams()

  const insuranceState = createSelector(
    (state: RootState) => state.bookings.booking,
    (rawData) => ({
      bookingInternalId: String(rawData.data?.internal_id || ''),
    })
  )

  const { bookingInternalId } = useAppSelector(insuranceState)

  const { data: insuranceStatus, isLoading: isLoadingInsuranceStatus } =
    useInsuranceStatus({ bookingId: bookingInternalId })

  const isPending = insuranceStatus === 'pending'
  const isInsuranceActive = insuranceStatus === 'active'

  const stopTimer = () => {
    console.info('insurance: stop pooling status')
    interval.stop()
  }

  const loadInsurance = () => {
    onUpdate?.()

    if (!router.get('id')) {
      stopTimer()
      return
    }
    console.info('insurance: pooling status')
    if (bookingInternalId) {
      dispatch({
        action: getAllBookingsInsurances({
          bookingInternalId,
          options: { refreshBooking: true },
        }),
      })
      dispatch({
        action: refreshUserMeta({
          bearerToken: claimStoredTokens().bearerToken || '',
        }),
      })
    }
  }

  React.useEffect(() => {
    if (bookingInternalId) {
      loadInsurance()
    }

    return interval.stop
  }, [])

  React.useEffect(() => {
    if (isPending && !isLoadingInsuranceStatus) {
      console.info('insurance: start pooling status')
      interval.start()
    }

    if (!isPending && isInsuranceActive) {
      stopTimer()
    }
  }, [isPending, isInsuranceActive])
}
